import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";

import DiffMatchPatch from "diff-match-patch";

import "../styles/Landing.css"

export default function Landing() {
  const nav = useNavigate();

  const paperMeta = useMemo(
    () => ({
      journal: "British Medical Journal",
      publicationDate: "April, 2023",
      author: "Jean Clearfield, MD",
    }),
    []
  );

  const originalText = useMemo(
    () =>
      `Methods\n\nWe conducted a retrospective observational cohort study using a prospectively maintained institutional spine registry. Adults evaluated for degenerative lumbar pathology (e.g., radiculopathy, spinal stenosis, degenerative spondylolisthesis) between January 2016 and December 2022 were eligible if they had lumbar MRI and baseline patient-reported outcomes (Oswestry Disability Index [ODI] and VAS pain). Exclusion criteria included prior spinal infection, malignancy, inflammatory spondyloarthropathy, acute traumatic injury, or incomplete imaging/outcome data. To mitigate confounding by indication, we performed propensity-score adjustment and selected the earliest qualifying scan for patients with multiple imaging studies.`,
    []
  );

  const summaryText = useMemo(
    () =>
      `Methods (Simplified + Summary)\n\nWe looked back at adults in an institutional spine registry that was updated over time. Patients were included if they were evaluated for degenerative low-back conditions between January 2016 and December 2022 and had a lumbar MRI plus baseline patient-reported outcomes. We measured function and pain using common questionnaires, including the Oswestry Disability Index and VAS pain. We excluded patients with spinal infection, malignancy, inflammatory spondyloarthropathy, acute trauma, or missing imaging or outcome information. To reduce bias from differences in who received which care, we adjusted using propensity scores and, when multiple imaging studies were available, we used the earliest scan that met the eligibility criteria.`,
    []
  );

  const [text, setText] = useState(originalText);
  const [cursorPos, setCursorPos] = useState(0);
  const [targetText, setTargetText] = useState(originalText);
  const [cursorMode, setCursorMode] = useState("idle");
  const [cursorPulseKey, setCursorPulseKey] = useState(0);
  const [robotWorking, setRobotWorking] = useState(false);
  const [insertedFlags, setInsertedFlags] = useState(() =>
    Array.from({ length: originalText.length }, () => false)
  );

  const textRef = useRef(originalText);
  const cursorRef = useRef(0);
  const animationIdRef = useRef(0);

  const renderWithInsertedUnderline = (segmentText, segmentFlags, keyPrefix, baseOffset) => {
    if (!segmentText) return null;

    const renderInsertedChunkIncludingSpaces = (chunk, chunkKeyPrefix, isPending) => {
      const parts = [];

      const push = (key, className, value) => {
        if (!value) return;

        parts.push(
          className ? (
            <span
              key={key}
              className={className}
            >
              {value}
            </span>
          ) : (
            <span key={key}>{value}</span>
          )
        );
      };

      // Keep newlines un-underlined, but allow interior spaces to be underlined.
      const lines = chunk.split("\n");
      for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
        const line = lines[lineIndex];

        // Underline from first non-space to last non-space (includes interior spaces).
        const first = line.search(/\S/);
        if (first === -1) {
          push(`${chunkKeyPrefix}-l${lineIndex}-blank`, null, line);
        } else {
          const lastNonSpace = line.length - 1 - [...line].reverse().join("").search(/\S/);
          const start = first;
          const end = lastNonSpace + 1;

          push(`${chunkKeyPrefix}-l${lineIndex}-pre`, null, line.slice(0, start));
          push(
            `${chunkKeyPrefix}-l${lineIndex}-ul`,
            isPending ? "insertedUnderline insertedUnderlinePending" : "insertedUnderline",
            line.slice(start, end)
          );
          push(`${chunkKeyPrefix}-l${lineIndex}-post`, null, line.slice(end));
        }

        if (lineIndex < lines.length - 1) {
          push(`${chunkKeyPrefix}-l${lineIndex}-nl`, null, "\n");
        }
      }

      return parts;
    };

    const nodes = [];
    let runStart = 0;
    let runInserted = Boolean(segmentFlags?.[0]);

    for (let i = 1; i <= segmentText.length; i += 1) {
      const atEnd = i === segmentText.length;
      const flag = atEnd ? runInserted : Boolean(segmentFlags?.[i]);

      if (atEnd || flag !== runInserted) {
        const chunk = segmentText.slice(runStart, i);
        const chunkEnd = (baseOffset ?? 0) + i;

        const isTypingThisChunk =
          robotWorking &&
          cursorMode === "type" &&
          chunkEnd === cursorPos &&
          chunkEnd > 0;

        nodes.push(
          runInserted ? (
            <span
              key={`${keyPrefix}-${runStart}`}
              className="insertedUnderlineChunk"
            >
              {renderInsertedChunkIncludingSpaces(chunk, `${keyPrefix}-${runStart}`, isTypingThisChunk)}
            </span>
          ) : (
            <span key={`${keyPrefix}-${runStart}`}>{chunk}</span>
          )
        );
        runStart = i;
        runInserted = flag;
      }
    }

    return nodes;
  };

  // Example programmatic update (safe: runs once).
  useEffect(() => {
    const t = window.setTimeout(() => {
      setTargetText(summaryText);
    }, 2000);
    return () => window.clearTimeout(t);
  }, [summaryText]);

  useEffect(() => {
    if (targetText === textRef.current) return;

    setRobotWorking(true);
    const animationId = ++animationIdRef.current;
    const dmp = new DiffMatchPatch();

    const startText = textRef.current;
    const diffs = dmp.diff_main(startText, targetText);
    dmp.diff_cleanupSemantic(diffs);

    const sleep = async (ms) => {
      const tick = 50;
      let remaining = ms;
      while (remaining > 0) {
        const slice = Math.min(tick, remaining);
        await new Promise((r) => window.setTimeout(r, slice));
        remaining -= slice;
      }
    };
    const rand = (min, max) => min + Math.random() * (max - min);

    const pauseForChar = (ch) => {
      if (ch === "\n") return 650;
      if (/[.!?]/.test(ch)) return 520;
      if (/[,:;]/.test(ch)) return 240;
      return 0;
    };

    const moveStepDelay = (ch) => {
      const base = rand(moveDelay * 0.55, moveDelay * 1.85);
      const microHesitation = Math.random() < 0.03 ? rand(120, 260) : 0;
      return base + pauseForChar(ch) + microHesitation;
    };
    const typeStepDelay = (ch, prevCh) => {
      const base = rand(typeDelay * 0.55, typeDelay * 1.95);
      const wordStartBonus = prevCh === " " ? rand(20, 90) : 0;
      const microHesitation = Math.random() < 0.035 ? rand(140, 320) : 0;
      return base + wordStartBonus + pauseForChar(ch) + microHesitation;
    };
    const deleteStepDelay = (deletedCh) => {
      const base = rand(deleteDelay * 0.45, deleteDelay * 1.75);
      const wordBoundaryBonus = deletedCh === " " ? rand(30, 130) : 0;
      const microHesitation = Math.random() < 0.02 ? rand(120, 240) : 0;
      return base + wordBoundaryBonus + microHesitation;
    };

    const longScanThreshold = 70;

    const jumpPause = (distance) => {
      // Human "find where to click" pause before a big jump.
      // Make it noticeably longer and more variable than per-char scanning.
      const extra = Math.min(2200, Math.max(0, distance - longScanThreshold) * 14);
      const base = rand(1200, 2300);
      const occasionalLongThink = Math.random() < 0.25 ? rand(600, 1500) : 0;
      return base + extra + occasionalLongThink;
    };

    // Tweak these for total duration (ms per character).
    // Intentionally slow scan + faster backspace.
    const moveDelay = 55;
    const deleteDelay = 35;
    const typeDelay = 95;

    // "Hold key" behavior: after N chars, movement/deletion becomes fast + consistent.
    const holdThreshold = 12;
    const holdMoveDelay = 34;
    const holdDeleteDelay = 28;

    const chars = Array.from(startText);
    const inserted = new Array(chars.length).fill(false);
    let cursor = 0;
    cursorRef.current = 0;
    setCursorPos(0);

    const commit = () => {
      const next = chars.join("");
      textRef.current = next;
      setText(next);
      setCursorPos(cursor);
      setInsertedFlags(inserted.slice());
    };

    commit();

    (async () => {
      for (const [op, data] of diffs) {
        if (animationIdRef.current !== animationId) return;
        const segment = String(data ?? "");

        if (op === 0) {
          if (segment.length > longScanThreshold) {
            setCursorMode("idle");
            await sleep(jumpPause(segment.length));
            setCursorMode("move");
            cursorRef.current = cursor;
            setCursorPos(cursor);
            await sleep(rand(40, 90));
          } else {
            setCursorMode("move");
            for (let i = 0; i < segment.length; i += 1) {
              if (animationIdRef.current !== animationId) return;
              cursor += 1;
              cursorRef.current = cursor;
              setCursorPos(cursor);
              await sleep(i < holdThreshold ? moveStepDelay(segment[i]) : holdMoveDelay);
            }
          }

          // Brief "thinking" pause occasionally on sentence ends.
          const last = segment[segment.length - 1];
          if (/[.!?]/.test(last)) {
            setCursorMode("idle");
            await sleep(rand(250, 450));
          }
        }

        if (op === -1) {
          // Move across the soon-to-be-deleted region, then backspace it.
          if (segment.length > longScanThreshold) {
            setCursorMode("idle");
            await sleep(jumpPause(segment.length));
            setCursorMode("move");
            cursor += segment.length;
            cursorRef.current = cursor;
            setCursorPos(cursor);
            await sleep(rand(40, 90));
          } else {
            setCursorMode("move");
            for (let i = 0; i < segment.length; i += 1) {
              if (animationIdRef.current !== animationId) return;
              cursor += 1;
              cursorRef.current = cursor;
              setCursorPos(cursor);
              await sleep(i < holdThreshold ? moveStepDelay(segment[i]) : holdMoveDelay);
            }
          }

          setCursorMode("delete");
          for (let i = 0; i < segment.length; i += 1) {
            if (animationIdRef.current !== animationId) return;
            cursor -= 1;
            const deletedCh = chars[cursor];
            chars.splice(cursor, 1);
            inserted.splice(cursor, 1);
            cursorRef.current = cursor;
            commit();
            setCursorPulseKey((k) => k + 1);
            await sleep(i < holdThreshold ? deleteStepDelay(deletedCh) : holdDeleteDelay);
          }

          setCursorMode("idle");
          await sleep(rand(180, 320));
          continue;
        }

        if (op === 1) {
          setCursorMode("type");
          for (let i = 0; i < segment.length; i += 1) {
            if (animationIdRef.current !== animationId) return;
            chars.splice(cursor, 0, segment[i]);
            inserted.splice(cursor, 0, true);
            cursor += 1;
            cursorRef.current = cursor;
            commit();
            setCursorPulseKey((k) => k + 1);
            const prevCh = i > 0 ? segment[i - 1] : chars[cursor - 2];
            await sleep(typeStepDelay(segment[i], prevCh));
          }

          setCursorMode("idle");
          await sleep(rand(220, 380));
        }
      }

      setCursorMode("idle");

      // Ensure final state is exact.
      if (animationIdRef.current !== animationId) return;
      textRef.current = targetText;
      setText(targetText);
      setInsertedFlags(inserted.slice());
      setCursorPos(cursorRef.current);
      setRobotWorking(false);
    })();

    return () => {
      // If we unmount or restart the effect, stop the spinner.
      setRobotWorking(false);
    };
  }, [targetText]);

  return (
    <div className="landing-hero">
      <div className="hero-left">
        <div className="hero-mission-label">OUR GOAL</div>
        <h1 className="hero-title">Clarity Through<br />Automated Analysis</h1>
        <p className="hero-desc">
          Spinal issues related to back, neck, and nerve pain, are notoriously complex.
          Multiple credible online sources often contradict each other, and can confuse patients seeking the truth.
          Our platform leverages machine learning to enable you to interpret peer-reviewed, open access research publications,
          so you can read straight from the source.
        </p>

        <button className="hero-learn-btn" onClick={() => nav('/how-it-works')}>
          Learn More
        </button>
      </div>

      <div className="hero-right">
        <div className="hero-illustration">
          <div className="hero-illustrationInner">
            <div className="heroFace heroFront">
              <div className={`robotBadge ${robotWorking ? "working" : "done"}`} aria-hidden="true">
                <div className="robotSpinner" />
                <svg className="robotIcon" viewBox="0 0 24 24" role="img" aria-label="Robot">
                  <rect x="6.5" y="7.5" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.6" />
                  <rect x="9" y="11" width="2" height="2" fill="currentColor" />
                  <rect x="13" y="11" width="2" height="2" fill="currentColor" />
                  <rect x="9" y="15" width="6" height="1.6" fill="currentColor" opacity="0.75" />
                  <path d="M12 5.5v2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" />
                </svg>
              </div>

              <div className="heroStudyText heroStudyTextTypewriter">
                <span className="robotTextGuard" aria-hidden="true" />
                {renderWithInsertedUnderline(
                  text.slice(0, cursorPos),
                  insertedFlags.slice(0, cursorPos),
                  "before",
                  0
                )}
                <span
                  key={cursorPulseKey}
                  className={`typeCursor ${cursorMode}${cursorPulseKey ? " pulse" : ""}`}
                  aria-hidden="true"
                >
                  |
                </span>
                {renderWithInsertedUnderline(
                  text.slice(cursorPos),
                  insertedFlags.slice(cursorPos),
                  "after",
                  cursorPos
                )}
              </div>

              <div className="heroPaperMeta" aria-label="Paper details">
                <span>
                  <strong>Journal:</strong> {paperMeta.journal}
                </span>
                <span className="heroPaperMetaSep" aria-hidden="true">
                  •
                </span>
                <span>
                  <strong>Published:</strong> {paperMeta.publicationDate}
                </span>
                <span className="heroPaperMetaSep" aria-hidden="true">
                  •
                </span>
                <span>
                  <strong>Author:</strong> {paperMeta.author}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}